import { ReactNode } from 'react';
import { EnhancedLightboxItem } from '../../net/softbox-types.ts';
import { IInterest, IInterestResponse } from '../../../pages/Root/services/api/interest/interest-model-definitions.ts';
import StoryCarousel from '../../components/StoryCarousel/StoryCarousel.tsx';
import { getSSRTranslation } from '../../../i18n.ts';
import StoryMedium from '../../components/StoryMedium/StoryMedium.tsx';
import StorySmall from '../../components/StorySmall/StorySmall.tsx';
import RowMultipleContainer from '../../containers/RowMultipleContainer/RowMultiplierContainer.tsx';
import { LayoutConfiguration } from '../LayoutConfiguration/layout-configuration-factory.ts';
import GamesTile from '../../components/GamesTile/GamesTile.tsx';
import GameMedium from '../../components/GameMedium/GameMedium.tsx';
import StoryLarge from '../../components/StoryLarge/StoryLarge.tsx';
import { i18n } from 'i18next';
import StoryTop from '@moment/components/StoryTop/StoryTop.tsx';
import ContentImpressionTracker from '../../components/ContentImpressionTracker/ContentImpressionTracker.tsx';

const constructRowMultiplierChildren = (
  settings: Record<string, string>,
  items: EnhancedLightboxItem[],
  interests: IInterestResponse,
  ClickerElement: React.FC,
  clickerPropsBuilder: (item: EnhancedLightboxItem) => Record<string, any>,
  i18n: i18n,
  source?: string,
  schedule?: string
) => {
  const output = [];
  if (settings.rowComposition) {
    for (const rowItem of settings.rowComposition) {
      output.push(
        createFeedComponent(
          {
            settings: {},
            component: rowItem,
          },
          items,
          interests,
          ClickerElement,
          clickerPropsBuilder,
          i18n,
          source,
          schedule
        ).component
      );
    }
  }
  return output;
};
export const createFeedComponent = (
  slot: LayoutConfiguration,
  items: EnhancedLightboxItem[],
  interests: IInterestResponse,
  ClickerElement: React.FC<any>,
  clickerPropsBuilder: (item: EnhancedLightboxItem) => Record<string, any>,
  i18n: i18n,
  source?: string,
  schedule?: string
): {
  component: React.ReactNode;
  item?: EnhancedLightboxItem;
} => {
  if (items.length === 0) {
    return { component: <></>, item: undefined };
  }

  const componentType = slot.component;
  const { settings } = slot;

  let component: ReactNode;

  let item: EnhancedLightboxItem | undefined;

  switch (componentType) {
    case 'StoryCarousel':
      item = items.shift()!;
      component = (
        <StoryCarousel
          key={item.uid}
          title={getSSRTranslation(item.title, i18n)}
          description={getSSRTranslation(item.summary, i18n)}
          imageList={item.previews}
          publisherLogo={item.brandLogo}
          darkPublisherLogo={item.brandLogoDark}
          publisherName={item.owner}
        />
      );
      break;
    case 'StoryLarge':
      item = items.shift()!;
      component = (
        <StoryLarge
          interestName={translateInterests(item.interests, interests, i18n)}
          key={item.uid}
          title={getSSRTranslation(item.title, i18n)}
          description={getSSRTranslation(item.summary, i18n)}
          imageList={item.previews}
          publisherLogo={item.brandLogo}
          publisherName={item.owner}
        />
      );
      break;
    case 'StoryTop':
      item = items.shift()!;
      component = (
        <StoryTop
          key={item.uid}
          title={getSSRTranslation(item.title, i18n)}
          description={getSSRTranslation(item.summary, i18n)}
          imageList={item.previews}
          publisherLogo={item.brandLogo}
          darkPublisherLogo={item.brandLogoDark}
          publisherName={item.owner}
        />
      );
      break;
    case 'StoryMedium':
      item = items.shift()!;
      component = (
        <StoryMedium
          key={item.uid}
          title={getSSRTranslation(item.title, i18n)}
          description={getSSRTranslation(item.summary, i18n)}
          imageList={item.previews}
          tags={translateInterests(item.interests, interests, i18n)}
          publisherLogo={item.brandLogo}
          darkPublisherLogo={item.brandLogoDark}
          publisherName={item.owner}
        />
      );
      break;
    case 'StorySmall':
      item = items.shift()!;
      component = (
        <StorySmall
            publisherLogo={item.brandLogo}
            darkPublisherLogo={item.brandLogoDark}
            publisherName={item.owner}
            key={item.uid}
            title={getSSRTranslation(item.title, i18n)}
            imageList={item.previews}
            category={translateInterests(item.interests, interests, i18n).join(
                ' '
            )}
            hideImage={slot.settings?.hideImage as boolean}
            hidePublisher={slot.settings?.hidePublisher as boolean}
            hideCategory={slot.settings?.hideInterest as boolean}
        />
      );
      break;
    case 'GamesTile':
      item = items.shift()!;
      component = (
        <GamesTile
          buttonText={i18n.t('playNow')}
          title={getSSRTranslation(item.title, i18n)}
          imageList={item.previews}
        />
      );
      break;
    case 'GameMedium':
      item = items.shift()!;
      component = (
        <GameMedium
          title={getSSRTranslation(item.title, i18n)}
          buttonText={i18n.t('playNow')}
          imageList={item.previews}
        />
      );
      break;
    case 'RowMultiple':
      component = (
        <RowMultipleContainer>
          {...constructRowMultiplierChildren(
            settings as Record<string, string>,
            items,
            interests,
            ClickerElement,
            clickerPropsBuilder,
            i18n,
            source,
            schedule
          )}
        </RowMultipleContainer>
      );
      break;
    default:
      component = <><h1>missing {componentType}</h1></>;
  }

  if (item) {
    const wrappedComponent = (
      <ContentImpressionTracker
        item={item}
        interests={interests}
        source={source}
        schedule={schedule}
      >
        {component}
      </ContentImpressionTracker>
    );

    component = (
      <ClickerElement key={item.uid} {...clickerPropsBuilder(item!)}>
        {wrappedComponent}
      </ClickerElement>
    );
  }

  return { component, item };
};

const translateInterests = (target: string[], interests: IInterestResponse, i18n: i18n) => {
  const output = [];
  for (let i = 0; i < target.length; i++) {
    const interest = interests.interests.find((interest: IInterest) => {
      return interest.uid === target[i];
    });
    if (interest) {
      output.push(getSSRTranslation(interest.name, i18n));
    }
  }
  return output.length > 0 ? [output[0]] : output;
};
